import * as React from 'react'
import { IContextProvider, IUserDetails } from '../../uxp'
import { Button, IconButton, Input, Label, Loading, Modal, SearchBox, useDebounce, useToast } from 'uxp/components';

import FileUploader from '../FileUploader/FileUploader';
import './ImagePicker.scss';
import useImageSearch from './useImageSearch';

interface IImagePickerProps {
    imageUrl: string,
    uxpContext: IContextProvider,
    onChange: (s: string) => void
}

export const ImagePicker: React.FunctionComponent<IImagePickerProps> = props => {
    const [isModalVisible, setModalVisible] = React.useState<boolean>(false)

    return (
        <div className="background-image-editor">
            <div className="inputs-wrapper">
                <Input
                    value={props.imageUrl}
                    onChange={s => { props.onChange(s) }}
                    className="widget-designer-input"
                    placeholder=''
                />
                <IconButton
                    className="search-button"
                    type='search'
                    onClick={() => { setModalVisible(true) }}
                />
            </div>

            <ImagePickerModal
                uxpContext={props.uxpContext}
                visible={isModalVisible}
                onChange={props.onChange}
                onClose={() => setModalVisible(false)}
                imageUrl={props.imageUrl}
            />
        </div>
    )
}

interface IImagePickerModalProps {
    visible: boolean,
    uxpContext: IContextProvider,
    onClose?: () => void,
    imageUrl: string,
    onChange: (imageUrl: string) => void,
    unplashQueryParameters?: {[key: string] : any}
}

export const ImagePickerModal: React.FunctionComponent<IImagePickerModalProps> = props => {
    const toast = useToast()
    const [pageNumber, setPageNumber] = React.useState<number>(1)
    
    const [query, setQuery] = React.useState<string>('');
    const debouncedQuery = useDebounce(query, 700);

    const {
        photos: unsplashPhotos,
        hasMore,
        loading,
        error
    } = useImageSearch(debouncedQuery, pageNumber, props.uxpContext, props.visible, props.unplashQueryParameters || {})

    const [usersPhotos, setUsersPhotos] = React.useState([]);

    React.useEffect(() => {
        setQuery('')
    }, [props.visible])

    return (
        <Modal
            headerContent={
                <div className='image-picker-header'>
                    <div className="title">Search Images</div>

                    <SearchBox value={query} onChange={setQuery} placeholder="enter some keywords to search for" />

                    <FileUploader
                        pathToUpload='notes/uploads/images/'
                        uxpContext={props.uxpContext}
                        title={"Upload image into your canvas"}
                        actionButtonText={"Insert this image"}
                        afterUpload={(url, filename) => {
                            setTimeout(() => {
                                setUsersPhotos([
                                    ...usersPhotos,
                                    {
                                        url: location.origin + url,
                                        fileName: filename,
                                    }
                                ])
                            }, 500)
                        }}
                    >
                        <Button
                            title={'Upload your image'}
                            onClick={() => { }}
                            icon="/Resources/Lucy/lucy-dashboard/assets/images/upload.svg" iconPosition="right"
                            className={"upload-button"}
                        />
                    </FileUploader>

                </div>
            }
            className={'image-picker-modal'}
            show={props.visible}
            onClose={props.onClose}
        >
            {loading && <Loading />}
            {
                usersPhotos.length > 0 && (
                    <>
                        <div className="image-gallery">
                            <div className="image-gallery-title">
                                Images uploaded by you
                            </div>
                            <ul className="images-container">
                                {
                                    usersPhotos.map((photo) => {
                                        return (
                                            <li className="image-wrapper">
                                                <a onClick={() => {
                                                    props.onChange(photo?.url)
                                                    props.onClose()
                                                }}>
                                                    <div className="overlay"></div>
                                                    <img src={photo?.url} loading="lazy" />
                                                </a>
                                            </li>
                                        )
                                    })
                                }

                                {/* Empty list item to prevent stretching last item unnessarily */}
                                <li className="empty-image-wrapper"></li>
                            </ul>
                        </div>

                        <div className="seperator"></div>
                    </>
                )
            }

            {
                unsplashPhotos.length > 0 && (
                    <div className="image-gallery">
                        {
                            usersPhotos.length > 0 &&
                            <div className="image-gallery-title">
                                Images from <a href="https://unsplash.com/">Unsplash.com</a>
                            </div>
                        }
                        <ul className="images-container">
                            {
                                unsplashPhotos.map((photo) => {
                                    return (
                                        <li className="image-wrapper">
                                            <a onClick={() => {
                                                props.onChange(photo?.urls?.regular)
                                                props.onClose()
                                            }}>
                                                <div className="overlay"></div>
                                                <img src={photo?.urls?.thumb} loading="lazy" />
                                                <div className="details fadeIn-top">
                                                    <h3>{photo?.user?.first_name}</h3>
                                                    <p>{photo?.description}</p>
                                                </div>
                                            </a>
                                        </li>
                                    )
                                })
                            }

                            {/* Empty list item to prevent stretching last item unnessary */}
                            <li className="empty-image-wrapper"></li>
                        </ul>

                        {
                            (!!hasMore && !loading) && <Button className="load-more" title='Load More Images...' onClick={() => setPageNumber((prev) => prev + 1)}/>
                        }
                        </div>
                )
            }
            {(unsplashPhotos.length == 0 && !loading) && <Label className='no-images'>No Images Found</Label>}
            {!loading && <Label className="powered-by">Powered by <a target='_blank' href="https://unsplash.com/">Unsplash.com</a></Label>}
        </Modal>
    )
}
